import { create } from 'zustand';
import {
  adTaxonomyProposedApi,
  catalogsApi,
  resolutionApi,
  type ProposalCountsBySource,
  type ProposalSource,
  type ProposedAdTaxonomyElement,
  type ProposedCanonical,
  type ProposedUOSExtension,
} from '@/services/api';

interface CatalogSummary {
  uos_classes: number;
  uos_classified: number;
  concept_types: number;
  ad_taxonomy_total: number;
  ad_taxonomy_form: number;
  ad_taxonomy_meaning: number;
  pending_by_source: ProposalCountsBySource;
  loaded: boolean;
  error: string | null;
}

interface ProposedQueueFilters {
  source_type: ProposalSource | '';
  // Phase 5 — when source_type='ad_taxonomy', sub-filter by axis
  // (creative_template / layout_archetype / visual_style / hook /
  //  angle / cta / proof). Empty string means "all ad axes".
  ad_axis_slug: string;
}

interface CatalogsState {
  summary: CatalogSummary;
  uosExtensions: ProposedUOSExtension[];
  uosLoading: boolean;
  adTaxonomyProposals: ProposedAdTaxonomyElement[];
  adTaxonomyProposalsLoading: boolean;

  queue: ProposedCanonical[];
  queueCountsBySource: ProposalCountsBySource;
  queueAdAxisCounts: Record<string, number>;
  queueLoading: boolean;
  queueFilters: ProposedQueueFilters;
  selectedProposal: ProposedCanonical | null;
  queueError: string | null;
  actingId: string | null;

  fetchSummary: () => Promise<void>;
  fetchUosExtensions: () => Promise<void>;
  fetchAdTaxonomyProposals: () => Promise<void>;
  fetchQueue: () => Promise<void>;
  setQueueFilters: (partial: Partial<ProposedQueueFilters>) => void;
  selectProposal: (p: ProposedCanonical | null) => void;
  actOnProposal: (
    item: ProposedCanonical,
    action: 'approve' | 'reject' | 'merge_into',
    opts?: {
      merge_into_id?: string;
      reclassify_to?: string;
      human_note?: string;
      reviewed_by?: string;
    },
  ) => Promise<void>;
  actOnUosExtension: (
    extensionId: string,
    verdict: 'approved' | 'rejected' | 'reclassified',
    opts?: { reclassify_to?: string; human_note?: string; reviewed_by?: string },
  ) => Promise<void>;
}

export const useCatalogsStore = create<CatalogsState>((set, get) => ({
  summary: {
    uos_classes: 0,
    uos_classified: 0,
    concept_types: 0,
    ad_taxonomy_total: 0,
    ad_taxonomy_form: 0,
    ad_taxonomy_meaning: 0,
    pending_by_source: { uos: 0, concept_type: 0, ad_taxonomy: 0 },
    loaded: false,
    error: null,
  },
  uosExtensions: [],
  uosLoading: false,
  adTaxonomyProposals: [],
  adTaxonomyProposalsLoading: false,

  queue: [],
  queueCountsBySource: { uos: 0, concept_type: 0, ad_taxonomy: 0 },
  queueAdAxisCounts: {},
  queueLoading: false,
  queueFilters: { source_type: '', ad_axis_slug: '' },
  selectedProposal: null,
  queueError: null,
  actingId: null,

  fetchSummary: async () => {
    const [schemaRes, distRes, conceptsRes, adProposedRes, adAllRes, pendingRes] =
      await Promise.allSettled([
        resolutionApi.getOntologicalSchema(),
        resolutionApi.getOntologicalDistribution(),
        resolutionApi.listCanonicals({ entity_type: 'concept_type', limit: 200 }),
        adTaxonomyProposedApi.list({ limit: 500 }),
        // The ad taxonomy API only exposes per-client summaries, so we use
        // the global proposals-list as an upper bound for total-elements.
        adTaxonomyProposedApi.list({ limit: 500 }),
        catalogsApi.summary().catch(() => null),
      ]);

    const classCount =
      schemaRes.status === 'fulfilled' ? schemaRes.value.class_count : 0;
    const classified =
      distRes.status === 'fulfilled' ? distRes.value.total_classified : 0;
    const conceptCount =
      conceptsRes.status === 'fulfilled' ? conceptsRes.value.count : 0;
    const adTaxonomyProps =
      adProposedRes.status === 'fulfilled' ? adProposedRes.value.proposals : [];
    const adTaxTotal =
      adAllRes.status === 'fulfilled' ? adAllRes.value.count : 0;
    const pending =
      pendingRes.status === 'fulfilled' && pendingRes.value
        ? pendingRes.value
        : ({ uos: 0, concept_type: 0, ad_taxonomy: 0 } as ProposalCountsBySource);

    const form = adTaxonomyProps.filter((n) => n.dimension === 'form').length;
    const meaning = adTaxonomyProps.filter((n) => n.dimension === 'meaning').length;

    set({
      summary: {
        uos_classes: classCount,
        uos_classified: classified,
        concept_types: conceptCount,
        ad_taxonomy_total: adTaxTotal,
        ad_taxonomy_form: form,
        ad_taxonomy_meaning: meaning,
        pending_by_source: pending,
        loaded: true,
        error: null,
      },
    });
  },

  fetchUosExtensions: async () => {
    set({ uosLoading: true });
    try {
      const res = await resolutionApi.getProposedExtensions();
      set({ uosExtensions: res.proposals });
    } catch {
      set({ uosExtensions: [] });
    } finally {
      set({ uosLoading: false });
    }
  },

  fetchAdTaxonomyProposals: async () => {
    set({ adTaxonomyProposalsLoading: true });
    try {
      const res = await adTaxonomyProposedApi.list({ limit: 200 });
      set({ adTaxonomyProposals: res.proposals });
    } catch {
      set({ adTaxonomyProposals: [] });
    } finally {
      set({ adTaxonomyProposalsLoading: false });
    }
  },

  fetchQueue: async () => {
    const { queueFilters } = get();
    set({ queueLoading: true, queueError: null });
    try {
      const res = await catalogsApi.listProposed({
        source_type: queueFilters.source_type || undefined,
        ad_axis_slug: queueFilters.ad_axis_slug || undefined,
        limit: 100,
      });
      set({
        queue: res.proposals,
        queueCountsBySource: res.counts_by_source,
        queueAdAxisCounts: res.ad_axis_counts || {},
        queueLoading: false,
      });
    } catch (err) {
      set({ queueError: (err as Error).message, queueLoading: false });
    }
  },

  setQueueFilters: (partial) => {
    set((s) => {
      const next = { ...s.queueFilters, ...partial };
      // Phase 5: clear ad_axis_slug whenever the source flips away from
      // ad_taxonomy so the chip rail starts clean each time the curator
      // returns to the ad_taxonomy bucket.
      if (
        partial.source_type !== undefined &&
        partial.source_type !== 'ad_taxonomy' &&
        partial.ad_axis_slug === undefined
      ) {
        next.ad_axis_slug = '';
      }
      return { queueFilters: next };
    });
    get().fetchQueue();
  },

  selectProposal: (p) => set({ selectedProposal: p }),

  actOnProposal: async (item, action, opts) => {
    set({ actingId: item.id });
    try {
      await catalogsApi.actOnProposal(item.id, { action, ...(opts ?? {}) });
      // Drop the acted-on item locally and advance to the next.
      const { queue, selectedProposal } = get();
      const idx = queue.findIndex((p) => p.id === item.id);
      const next = queue.slice();
      next.splice(idx, 1);
      const nextSelected =
        selectedProposal?.id === item.id
          ? next[Math.min(idx, next.length - 1)] ?? null
          : selectedProposal;
      set({ queue: next, selectedProposal: nextSelected });
      // Refresh counts from server so summary stays in sync.
      get().fetchSummary();
    } finally {
      set({ actingId: null });
    }
  },

  actOnUosExtension: async (extensionId, verdict, opts) => {
    set({ actingId: `uos:${extensionId}` });
    try {
      await resolutionApi.actOnProposedExtension(extensionId, {
        verdict,
        reclassify_to: opts?.reclassify_to,
        human_note: opts?.human_note,
        reviewed_by: opts?.reviewed_by,
      });
      // Remove from local list.
      const next = get().uosExtensions.filter((e) => e.id !== extensionId);
      set({ uosExtensions: next });
      get().fetchSummary();
    } finally {
      set({ actingId: null });
    }
  },
}));
